import { Button } from '@shared/components/ui/button';
import { ConfirmModal, type ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/popover/Popover';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmarkCircle,
  faCircleCheck,
  faRotateLeft,
  faTag,
  faChevronDown,
  faTimes,
  faCircle,
} from '@fortawesome/pro-light-svg-icons';
import { memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CrmStatusModel, TagModel } from '../../models/CRM';

interface CRMSelectionToolbarProps {
  selectedCount: number;
  cancelableCount: number;
  confirmableCount: number;
  uncancelableCount: number;
  statusList: CrmStatusModel[];
  tagList: TagModel[];
  isLoading?: boolean;
  onClose: () => void;
  onCancelSelected: () => Promise<void>;
  onConfirmSelected: () => Promise<void>;
  onUncancelSelected: () => Promise<void>;
  onChangeStatus: (statusId: number | null) => Promise<void>;
  onAddTags: (tagIds: number[]) => Promise<void>;
}

export const CRMSelectionToolbar = memo(
  ({
    selectedCount,
    cancelableCount,
    confirmableCount,
    uncancelableCount,
    statusList,
    tagList,
    isLoading = false,
    onClose,
    onCancelSelected,
    onConfirmSelected,
    onUncancelSelected,
    onChangeStatus,
    onAddTags,
  }: CRMSelectionToolbarProps) => {
    const { t } = useTranslation('crmModule');
    const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;

    const [selectedTagIds, setSelectedTagIds] = useState<Set<number>>(new Set());
    const [isTagsOpen, setIsTagsOpen] = useState(false);

    const cancelModalRef = useRef<ConfirmModalRef>(null);
    const confirmModalRef = useRef<ConfirmModalRef>(null);
    const uncancelModalRef = useRef<ConfirmModalRef>(null);
    const statusModalRef = useRef<ConfirmModalRef>(null);

    const handleCancel = () => {
      if (cancelableCount === 0) return;
      cancelModalRef.current?.open(
        onCancelSelected,
        undefined,
        t('selection.confirmCancel.title', 'Cancelar pedidos'),
        t('selection.confirmCancel.message', { count: cancelableCount }),
      );
    };

    const handleConfirm = () => {
      if (confirmableCount === 0) return;
      confirmModalRef.current?.open(
        onConfirmSelected,
        undefined,
        t('selection.confirmConfirm.title', 'Confirmar pedidos'),
        t('selection.confirmConfirm.message', { count: confirmableCount }),
      );
    };

    const handleUncancel = () => {
      if (uncancelableCount === 0) return;
      uncancelModalRef.current?.open(
        onUncancelSelected,
        undefined,
        t('selection.confirmUncancel.title', 'Descancelar pedidos'),
        t('selection.confirmUncancel.message', { count: uncancelableCount }),
      );
    };

    const handleStatusSelect = (status: { id: number | null; name: string }) => {
      statusModalRef.current?.open(
        () => onChangeStatus(status.id),
        undefined,
        t('selection.confirmChangeStatus.title', 'Cambiar estado'),
        t('selection.confirmChangeStatus.message', { count: selectedCount, status: status.name }),
      );
    };

    const handleApplyTags = async () => {
      if (selectedTagIds.size === 0) return;
      await onAddTags([...selectedTagIds]);
      setSelectedTagIds(new Set());
      setIsTagsOpen(false);
    };

    const toggleTag = (tagId: number) => {
      setSelectedTagIds((prev) => {
        const next = new Set(prev);
        if (next.has(tagId)) {
          next.delete(tagId);
        } else {
          next.add(tagId);
        }
        return next;
      });
    };

    if (selectedCount === 0) return null;

    const tagPickerContent = (
      <PopoverContent className="w-64 p-2 border-card-border bg-card-bg" align="end">
        <div className="max-h-48 overflow-y-auto space-y-1 mb-2">
          {tagList.length === 0 ? (
            <p className="text-sm text-text-secondary px-2 py-1">{t('selection.noTags', 'Sin etiquetas')}</p>
          ) : (
            tagList.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => toggleTag(tag.id)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left hover:bg-hover-press transition-colors ${
                  selectedTagIds.has(tag.id) ? 'bg-primary/10 text-primary' : ''
                }`}
              >
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: tag.color ?? '#6b7280' }}
                />
                {tag.name}
              </button>
            ))
          )}
        </div>
        <Button
          size="sm"
          className="w-full"
          disabled={selectedTagIds.size === 0 || isLoading}
          onClick={handleApplyTags}
        >
          {t('selection.apply', 'Aplicar')}
        </Button>
      </PopoverContent>
    );

    const desktopActions = (
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={handleCancel}
          disabled={cancelableCount === 0 || isLoading}
          className={`border-card-border bg-transparent ${cancelableCount === 0 ? 'opacity-50' : ''}`}
        >
          <FontAwesomeIcon icon={faXmarkCircle} className="w-3 h-3 mr-1 text-error" />
          {t('selection.cancel', 'Cancelar')} ({cancelableCount})
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={handleConfirm}
          disabled={confirmableCount === 0 || isLoading}
          className={`border-card-border bg-transparent ${confirmableCount === 0 ? 'opacity-50' : ''}`}
        >
          <FontAwesomeIcon icon={faCircleCheck} className="w-3 h-3 mr-1 text-success" />
          {t('selection.confirm', 'Confirmar')} ({confirmableCount})
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={handleUncancel}
          disabled={uncancelableCount === 0 || isLoading}
          className={`border-card-border bg-transparent ${uncancelableCount === 0 ? 'opacity-50' : ''}`}
        >
          <FontAwesomeIcon icon={faRotateLeft} className="w-3 h-3 mr-1" />
          {t('selection.uncancel', 'Descancelar')} ({uncancelableCount})
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" disabled={isLoading} className="border-card-border bg-transparent">
              <FontAwesomeIcon icon={faCircle} className="w-3 h-3 mr-1" />
              {t('selection.changeStatus', 'Cambiar estado')}
              <FontAwesomeIcon icon={faChevronDown} className="ml-1 w-2.5 h-2.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52 border-card-border max-h-64 overflow-y-auto">
            <DropdownMenuItem onClick={() => handleStatusSelect({ id: null, name: t('table.noStatus', 'Sin estado') })}>
              <span className="w-3 h-3 rounded-full border border-card-border mr-2 flex-shrink-0" />
              {t('table.noStatus', 'Sin estado')}
            </DropdownMenuItem>
            {statusList.map((status) => (
              <DropdownMenuItem
                key={status.id}
                onClick={() => handleStatusSelect({ id: status.id, name: status.name })}
              >
                <span
                  className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                  style={{ backgroundColor: status.color ?? '#6b7280' }}
                />
                {status.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Popover open={isTagsOpen} onOpenChange={setIsTagsOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" disabled={isLoading} className="border-card-border bg-transparent">
              <FontAwesomeIcon icon={faTag} className="w-3 h-3 mr-1" />
              {t('selection.addTags', 'Añadir etiquetas')}
              <FontAwesomeIcon icon={faChevronDown} className="ml-1 w-2.5 h-2.5" />
            </Button>
          </PopoverTrigger>
          {tagPickerContent}
        </Popover>
      </div>
    );

    const mobileActions = (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="border-card-border bg-transparent">
            {t('selection.actions', 'Acciones')}
            <FontAwesomeIcon icon={faChevronDown} className="ml-2 w-3 h-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52 border-card-border">
          <DropdownMenuItem
            onClick={handleCancel}
            disabled={cancelableCount === 0}
            className={cancelableCount === 0 ? 'opacity-50' : ''}
          >
            <FontAwesomeIcon icon={faXmarkCircle} className="mr-2 w-3.5 text-error" />
            {t('selection.cancel', 'Cancelar')} ({cancelableCount})
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleConfirm}
            disabled={confirmableCount === 0}
            className={confirmableCount === 0 ? 'opacity-50' : ''}
          >
            <FontAwesomeIcon icon={faCircleCheck} className="mr-2 w-3.5 text-success" />
            {t('selection.confirm', 'Confirmar')} ({confirmableCount})
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleUncancel}
            disabled={uncancelableCount === 0}
            className={uncancelableCount === 0 ? 'opacity-50' : ''}
          >
            <FontAwesomeIcon icon={faRotateLeft} className="mr-2 w-3.5" />
            {t('selection.uncancel', 'Descancelar')} ({uncancelableCount})
          </DropdownMenuItem>
          {statusList.map((status) => (
            <DropdownMenuItem key={status.id} onClick={() => handleStatusSelect({ id: status.id, name: status.name })}>
              <span
                className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                style={{ backgroundColor: status.color ?? '#6b7280' }}
              />
              {status.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );

    return (
      <>
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-card-bg border border-card-border rounded-lg p-2 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 bg-primary text-white text-sm rounded-full flex items-center justify-center">
                  {selectedCount}
                </span>
                <span className="text-sm font-medium">{t('selection.selected', 'seleccionados')}</span>
              </div>

              {isMobile ? mobileActions : desktopActions}

              <Button variant="ghost" size="sm" onClick={onClose} className="p-0 hover:bg-transparent">
                <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <ConfirmModal ref={cancelModalRef} />
        <ConfirmModal ref={confirmModalRef} />
        <ConfirmModal ref={uncancelModalRef} />
        <ConfirmModal ref={statusModalRef} />
      </>
    );
  },
);

CRMSelectionToolbar.displayName = 'CRMSelectionToolbar';
